import { HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { AuthService } from '../services/auth.service';
import { ConfigService } from '../services/config.service';
import { APP_CONSTANTS } from '../constants/app.constants';
import { throwError } from 'rxjs';

export const authInterceptor: HttpInterceptorFn = (req, next) => {
  const authService = inject(AuthService);
  const configService = inject(ConfigService);

  const isConfigRequest = req.url.includes('/config/environment');

  console.log('🔐 AuthInterceptor - Request URL:', req.url);
  console.log('🔐 AuthInterceptor - Is config request:', isConfigRequest);

  // Skip auth header for config requests
  if (isConfigRequest) {
    console.log('🔐 AuthInterceptor - Config request, proceeding without auth header');
    return next(req);
  }

  let token: string | null = null;

  // Determine which token to use based on the URL
  if (req.url.includes('/auth/spotify/connect')) {
    // OAuth connection endpoints need the main auth token (user must be logged in)
    token = authService.getToken();
    console.log('🔐 AuthInterceptor - Using main auth token for Spotify connect endpoint');
  } else if (req.url.includes('/auth/spotify')) {
    // Other Spotify auth endpoints (like callback) don't need a token
    console.log('🔐 AuthInterceptor - No token needed for Spotify auth endpoint');
    return next(req);
  } else if (req.url.includes('/spotify/')) {
    // Spotify data endpoints: use Spotify access token directly in Authorization header
    const spotifyToken = localStorage.getItem(APP_CONSTANTS.STORAGE_KEYS.SPOTIFY_TOKEN);
    if (spotifyToken) {
      // Validate token format
      if (spotifyToken.startsWith('ey')) {
        console.error('🚨 AuthInterceptor - Invalid Spotify token format (JWT detected)');
        localStorage.removeItem(APP_CONSTANTS.STORAGE_KEYS.SPOTIFY_TOKEN);
        return throwError(() => new Error('Invalid Spotify token format'));
      }

      token = spotifyToken;
      console.log('🔐 AuthInterceptor - Using Spotify access token');
      console.log('🔐 AuthInterceptor - Token preview:', spotifyToken.substring(0, 20) + '...');
    } else {
      console.warn('⚠️ AuthInterceptor - No Spotify token available');
      return throwError(() => new Error('No Spotify token available'));
    }
  } else if (req.url.includes('/discogs/')) {
    // Discogs data endpoints: use Discogs access token directly in Authorization header
    const discogsToken = localStorage.getItem(APP_CONSTANTS.STORAGE_KEYS.DISCOGS_TOKEN);
    if (discogsToken) {
      token = discogsToken; // Use Discogs token directly as Authorization Bearer
      console.log('🔐 AuthInterceptor - Using Discogs access token for Authorization header');
      console.log('🔐 AuthInterceptor - Discogs token exists:', !!discogsToken);
      console.log('🔐 AuthInterceptor - Discogs token preview:', discogsToken?.substring(0, 30) + '...');
    } else {
      console.log('🔐 AuthInterceptor - Warning: No Discogs token available for /discogs/ endpoint');
      token = null;
    }
  } else {
    // All other endpoints use the main auth token
    token = authService.getToken();
    console.log('🔐 AuthInterceptor - Using main auth token for general endpoints');
  }

  console.log('🔐 AuthInterceptor - Token exists:', !!token);

  // Build headers based on endpoint type
  let headers = req.headers;

  // Add Authorization header if we have a token
  if (token) {
    console.log('🔐 AuthInterceptor - Adding Authorization header');
    headers = headers.set('Authorization', `Bearer ${token}`);
  }

  // Clone request with updated headers if any were added
  if (headers !== req.headers) {
    const modifiedReq = req.clone({ headers });
    return next(modifiedReq);
  }

  console.log('🔐 AuthInterceptor - No headers to add, proceeding with original request');
  return next(req);
};
